import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Form,
    Row,
    Col,
    Button,
    Modal,
    Badge,
    Card,
    Alert,
    Spinner,
} from 'react-bootstrap';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './DataTable.css';

const DailyRepoting = () => {
    const baseurl = process.env.REACT_APP_API_URL;
    const [reports, setReports] = useState([]);
    const [isTableLoading, setIsTableLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedReport, setSelectedReport] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [analytics, setAnalytics] = useState({});
    const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [groupLeader, setGroupLeader] = useState('');
    const [teamLeader, setTeamLeader] = useState('');
    const [city, setCity] = useState('');
    const [filterDate, setFilterDate] = useState('');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 50;

    const fetchReports = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('No authentication token found. Please log in again.');
            setIsTableLoading(false);
            return;
        }

        setIsTableLoading(true);
        setError('');
        try {
            const params = {
                per_page: itemsPerPage,
                page: currentPage,
                name: searchTerm || undefined,
                group_leader: groupLeader || undefined,
                team_leader: teamLeader || undefined,
                city: city || undefined,
                date_time: filterDate || undefined,
            };

            Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

            const response = await axios.post(`${baseurl}/daily-reporting`, params, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            setReports(response.data.data || []);
            setAnalytics(response.data.analytics || {});
            setTotalPages(response.data.pagination?.last_page || 1);
            setTotalItems(response.data.pagination?.total || 0);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch reports');
        } finally {
            setIsTableLoading(false);
        }
    }, [baseurl, currentPage, searchTerm, groupLeader, teamLeader, city, filterDate]);

    useEffect(() => {
        fetchReports();
    }, [currentPage, fetchReports]);

    const handleRowClick = (report) => {
        setSelectedReport(report);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedReport(null);
    };

    const getMeetingPlaceBadge = (meetingPlace) => {
        const place = (meetingPlace || '').toLowerCase();
        let bgClass = 'bg-primary';
        if (place === 'home') bgClass = 'bg-success';
        if (place === 'field') bgClass = 'bg-warning text-dark';
        if (place === 'group') bgClass = 'bg-info text-dark';

        return <Badge className={`${bgClass} px-2 py-1`}>{meetingPlace || 'N/A'}</Badge>;
    };

    // Export to CSV
    const exportToCSV = () => {
        const headers = ['Name', 'Group Leader', 'Team Leader', 'Reference By', 'Plan By', 'City', 'Occupation', 'Qualification', 'Date Time', 'Meeting Place', 'Age', 'Mobile'];
        const csvContent = [
            headers.join(','),
            ...reports.map(report => [
                `"${report.name || ''}"`,
                `"${report.group_leader || ''}"`,
                `"${report.team_leader || ''}"`,
                `"${report.reference_by || ''}"`,
                `"${report.plan_by || ''}"`,
                `"${report.city || ''}"`,
                `"${report.occupation || ''}"`,
                `"${report.qualification || ''}"`,
                `"${report.date_time ? new Date(report.date_time).toLocaleDateString() : ''}"`,
                `"${report.meeting_place || ''}"`,
                `"${report.age || ''}"`,
                `"${report.mobile || ''}"`,
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `daily-reports-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export to PDF
    const exportToPDF = () => {
        try {
            if (reports.length === 0) {
                alert('No data available to export');
                return;
            }

            const doc = new jsPDF('landscape');
            doc.setFontSize(16);
            doc.text('AWPL Daily Work Reports', 20, 20);

            const tableColumn = ['Name', 'Group Leader', 'Team Leader', 'Reference By', 'Plan By', 'City', 'Occupation', 'Qualification', 'Date Time', 'Meeting Place', 'Age', 'Mobile'];
            const tableRows = reports.map(report => [
                report.name || '',
                report.group_leader || '',
                report.team_leader || '',
                report.reference_by || '',
                report.plan_by || '',
                report.city || '',
                report.occupation || '',
                report.qualification || '',
                report.date_time ? new Date(report.date_time).toLocaleDateString() : '',
                report.meeting_place || '',
                report.age || '',
                report.mobile || '',
            ]);

            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 30,
                theme: 'striped',
                headStyles: { fillColor: [79, 70, 229], textColor: [255, 255, 255] },
                styles: { fontSize: 8, cellPadding: 2 },
            });

            doc.save(`daily-reports-${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setGroupLeader('');
        setTeamLeader('');
        setCity('');
        setFilterDate('');
        setCurrentPage(1);
    };

    const handleApplyFilters = () => {
        setCurrentPage(1);
        fetchReports();
    };

    return (
        <Container fluid className="reporting-container">
            {/* Header Box */}
            <div className="reporting-header-box">
                <div className="reporting-title-wrap">
                    <h2 className="reporting-title">
                        <span>📝</span> Daily Work Reporting
                    </h2>
                    <p className="reporting-subtitle">Manage, search, and audit field leader activities & entries</p>
                </div>
                <div className="reporting-actions-wrap">
                    <button className="btn-export-pdf" onClick={exportToPDF}>
                        <span>📄</span> Export PDF
                    </button>
                    <button className="btn-print-data" onClick={exportToCSV}>
                        <span>📊</span> Export CSV
                    </button>
                </div>
            </div>

            {/* Analytics KPI Metrics */}
            <div className="analytics-metrics-grid">
                <div className="analytics-stat-card">
                    <div className="analytics-stat-info">
                        <span className="analytics-stat-label">Today's Entries</span>
                        <span className="analytics-stat-num">{analytics.total_today_entries || 0}</span>
                    </div>
                    <div className="analytics-stat-icon">⚡</div>
                </div>

                <div className="analytics-stat-card">
                    <div className="analytics-stat-info">
                        <span className="analytics-stat-label">Week's Entries</span>
                        <span className="analytics-stat-num">{analytics.total_week_entries || 0}</span>
                    </div>
                    <div className="analytics-stat-icon">📅</div>
                </div>

                <div className="analytics-stat-card">
                    <div className="analytics-stat-info">
                        <span className="analytics-stat-label">Month's Entries</span>
                        <span className="analytics-stat-num">{analytics.total_month_entries || 0}</span>
                    </div>
                    <div className="analytics-stat-icon">📈</div>
                </div>

                <div className="analytics-stat-card">
                    <div className="analytics-stat-info">
                        <span className="analytics-stat-label">Total Submissions</span>
                        <span className="analytics-stat-num">{analytics.total_no_of_entries || 0}</span>
                    </div>
                    <div className="analytics-stat-icon">🏆</div>
                </div>
            </div>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            {/* Filter Card */}
            <div className="filter-panel-card">
                <div className="filter-card-title">
                    <span>🔍</span> Filter & Search Records
                </div>
                <div className="filter-grid">
                    <div className="filter-field">
                        <label className="filter-label">Leader / Candidate Name</label>
                        <Form.Control
                            type="text"
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="filter-field">
                        <label className="filter-label">Group Leader</label>
                        <Form.Control
                            type="text"
                            placeholder="Filter group leader..."
                            value={groupLeader}
                            onChange={(e) => setGroupLeader(e.target.value)}
                        />
                    </div>

                    <div className="filter-field">
                        <label className="filter-label">Team Leader</label>
                        <Form.Control
                            type="text"
                            placeholder="Filter team leader..."
                            value={teamLeader}
                            onChange={(e) => setTeamLeader(e.target.value)}
                        />
                    </div>

                    <div className="filter-field">
                        <label className="filter-label">City</label>
                        <Form.Control
                            type="text"
                            placeholder="Filter city..."
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />
                    </div>

                    <div className="filter-field">
                        <label className="filter-label">Date</label>
                        <Form.Control
                            type="date"
                            value={filterDate}
                            onChange={(e) => setFilterDate(e.target.value)}
                        />
                    </div>

                    <div className="filter-actions">
                        <button className="btn-filter-apply" onClick={handleApplyFilters}>
                            <span>🔍</span> Search
                        </button>
                        <button className="btn-filter-reset" onClick={handleClearFilters}>
                            <span>↺</span> Reset
                        </button>
                    </div>
                </div>
            </div>

            {/* Data Table Card */}
            <div className="table-data-card">
                {/* Table Toolbar & View Switcher */}
                <div className="table-toolbar-box">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="fw-bold small text-muted">View Mode:</span>
                        <div className="view-mode-toggle-group">
                            <button
                                className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setViewMode('table')}
                            >
                                <span>📊</span> Table View
                            </button>
                            <button
                                className={`view-mode-btn ${viewMode === 'cards' ? 'active' : ''}`}
                                onClick={() => setViewMode('cards')}
                            >
                                <span>🗂️</span> Card View
                            </button>
                        </div>
                    </div>
                    {viewMode === 'table' && (
                        <div className="mobile-swipe-hint d-md-none">
                            <span>👉</span> Swipe horizontally to view all columns
                        </div>
                    )}
                </div>

                {isTableLoading ? (
                    <div className="d-flex flex-column justify-content-center align-items-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <span className="mt-3 text-muted">Retrieving daily reports...</span>
                    </div>
                ) : (
                    <>
                        {/* Table View (Works on Desktop & Mobile with Horizontal Scroll) */}
                        {viewMode === 'table' && (
                            <div className="table-responsive-wrapper">
                                <table className="modern-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Group Leader</th>
                                            <th>Team Leader</th>
                                            <th>Reference By</th>
                                            <th>Plan By</th>
                                            <th>City</th>
                                            <th>Occupation</th>
                                            <th>Qualification</th>
                                            <th>Date</th>
                                            <th>Meeting Place</th>
                                            <th>Mobile</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reports.length > 0 ? (
                                            reports.map((report) => (
                                                <tr
                                                    key={report.id}
                                                    className="table-row-interactive"
                                                    onClick={() => handleRowClick(report)}
                                                >
                                                    <td>
                                                        <div className="user-cell">
                                                            <div className="user-avatar-pill">
                                                                {report.name ? report.name.charAt(0).toUpperCase() : 'U'}
                                                            </div>
                                                            <div className="user-cell-meta">
                                                                <span className="user-name-bold">{report.name || 'Unnamed'}</span>
                                                                <span className="user-sub-id">Age: {report.age || 'N/A'}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{report.group_leader || '-'}</td>
                                                    <td>{report.team_leader || '-'}</td>
                                                    <td>{report.reference_by || '-'}</td>
                                                    <td>{report.plan_by || '-'}</td>
                                                    <td>{report.city || '-'}</td>
                                                    <td>{report.occupation || '-'}</td>
                                                    <td>{report.qualification || '-'}</td>
                                                    <td>{report.date_time ? new Date(report.date_time).toLocaleDateString() : '-'}</td>
                                                    <td>{getMeetingPlaceBadge(report.meeting_place)}</td>
                                                    <td>{report.mobile || '-'}</td>
                                                    <td>
                                                        <button
                                                            className="btn-view-details"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRowClick(report);
                                                            }}
                                                        >
                                                            Details
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="12">
                                                    <div className="empty-table-state">
                                                        <div className="empty-icon">📋</div>
                                                        <div className="empty-title">No Daily Reports Found</div>
                                                        <div className="empty-desc">No records matched your search parameters.</div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Card View */}
                        {viewMode === 'cards' && (
                            <div className="p-3">
                                {reports.length > 0 ? (
                                    <Row className="g-3">
                                        {reports.map((report) => (
                                            <Col key={report.id} xs={12} md={6} xl={4}>
                                                <Card
                                                    className="h-100 shadow-sm border"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleRowClick(report)}
                                                >
                                                    <Card.Body className="p-3">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div>
                                                                <h6 className="fw-bold mb-0 text-primary">{report.name || 'Unnamed'}</h6>
                                                                <small className="text-muted">Age: {report.age || 'N/A'} • {report.city || '-'}</small>
                                                            </div>
                                                            {getMeetingPlaceBadge(report.meeting_place)}
                                                        </div>
                                                        <hr className="my-2 opacity-25" />
                                                        <div className="small text-secondary mb-1">
                                                            <strong>Group Leader:</strong> {report.group_leader || '-'}
                                                        </div>
                                                        <div className="small text-secondary mb-1">
                                                            <strong>Team Leader:</strong> {report.team_leader || '-'}
                                                        </div>
                                                        <div className="small text-secondary mb-1">
                                                            <strong>Reference By:</strong> {report.reference_by || '-'}
                                                        </div>
                                                        <div className="small text-secondary mb-2">
                                                            <strong>Date:</strong> {report.date_time ? new Date(report.date_time).toLocaleDateString() : '-'}
                                                        </div>
                                                        <Button size="sm" variant="outline-primary" className="w-100 mt-2">
                                                            View Full Details
                                                        </Button>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : (
                                    <div className="empty-table-state">
                                        <div className="empty-icon">📋</div>
                                        <div className="empty-title">No Daily Reports Found</div>
                                        <div className="empty-desc">Try clearing or adjusting filters.</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Pagination Bar */}
                        <div className="pagination-container-box">
                            <span className="pagination-summary-text">
                                Showing {reports.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} to{' '}
                                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} total records
                            </span>
                            <div className="pagination-controls">
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage <= 1}
                                    onClick={() => setCurrentPage(1)}
                                    title="First Page"
                                >
                                    «
                                </button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage <= 1}
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    title="Previous Page"
                                >
                                    ‹
                                </button>
                                <span className="px-2 fw-bold text-muted small">
                                    Page {currentPage} of {totalPages || 1}
                                </span>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    title="Next Page"
                                >
                                    ›
                                </button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage(totalPages)}
                                    title="Last Page"
                                >
                                    »
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Detail Modal */}
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                size="lg"
                centered
                className="detail-modal-custom"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <span>📝</span> Daily Report Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedReport && (
                        <div className="modal-details-grid">
                            <div className="detail-field-box">
                                <span className="detail-field-label">Candidate Name</span>
                                <span className="detail-field-val">{selectedReport.name || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Group Leader</span>
                                <span className="detail-field-val">{selectedReport.group_leader || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Team Leader</span>
                                <span className="detail-field-val">{selectedReport.team_leader || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Reference By</span>
                                <span className="detail-field-val">{selectedReport.reference_by || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Plan By</span>
                                <span className="detail-field-val">{selectedReport.plan_by || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">City</span>
                                <span className="detail-field-val">{selectedReport.city || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Occupation</span>
                                <span className="detail-field-val">{selectedReport.occupation || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Qualification</span>
                                <span className="detail-field-val">{selectedReport.qualification || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Meeting Place</span>
                                <span className="detail-field-val">{selectedReport.meeting_place || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Age</span>
                                <span className="detail-field-val">{selectedReport.age || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Mobile</span>
                                <span className="detail-field-val">{selectedReport.mobile || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Recorded Date & Time</span>
                                <span className="detail-field-val">
                                    {selectedReport.date_time ? new Date(selectedReport.date_time).toLocaleString() : '-'}
                                </span>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default DailyRepoting;